import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Center, Chip, Field, Spinner } from '@barev/ui';
import { confirmDialog, errorText, haptic, scanQr, useI18n, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { PageHead } from '../lib.tsx';
import { Qr } from '../qr.tsx';

const LEVEL_NAME: Record<string, string> = { BAREV: 'Barev', BAREV_PLUS: 'Barev+', ELITE: 'Barev Elite' };
type L = Record<string, string>;

/**
 * Кабинет партнёра клуба: сканер QR клиента → уровень и что положено, списание лимитных предложений.
 * Собственник дополнительно видит сотрудников и приглашает новых по QR.
 */
export function Partner({ partners }: { partners: { id: string; name: string; role: 'OWNER' | 'STAFF' }[] }) {
  const { t, locale, dt } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [partnerId, setPartnerId] = useState(partners[0]!.id);
  const cab = useQuery({ ...trpc.pro.partner.cabinet.queryOptions({ partnerId }), refetchInterval: 30_000 });
  const [manual, setManual] = useState('');
  const [invite, setInvite] = useState<{ url: string; expiresAt: Date } | null>(null);
  const name = (l: L | null | undefined) => (l ? l[locale] || l.ru || '' : '');
  const fail = (e: unknown) => {
    haptic.error();
    toast(errorText(t, e), 'error');
  };
  const check = useMutation(trpc.pro.partner.check.mutationOptions({ onError: fail }));
  const [result, setResult] = useState<NonNullable<typeof check.data> | null>(null);
  const redeem = useMutation(
    trpc.pro.partner.redeem.mutationOptions({
      onSuccess: (o) => {
        haptic.ok();
        toast(t('pro.partner.redeemed', { offer: name(o.title) }));
        // остаток в карточке проверки — без повторной проверки (она записалась бы как ещё один визит)
        setResult((cur) => (cur ? { ...cur, offers: cur.offers.map((x) => (x.id === o.id ? { ...x, remaining: o.remaining } : x)) } : cur));
        void qc.invalidateQueries({ queryKey: trpc.pro.partner.cabinet.queryKey() });
      },
      onError: fail,
    }),
  );
  const makeInvite = useMutation(trpc.pro.partner.invite.mutationOptions({ onSuccess: setInvite, onError: fail }));
  const remove = useMutation(trpc.pro.partner.removeMember.mutationOptions({ onSuccess: () => void qc.invalidateQueries(), onError: fail }));
  function run(token: string) {
    setResult(null);
    check.mutate(
      { token, partnerId },
      {
        onSuccess: (r) => {
          haptic.ok();
          setResult(r);
          void qc.invalidateQueries({ queryKey: trpc.pro.partner.cabinet.queryKey() });
        },
      },
    );
  }
  async function scan() {
    const p = scanQr(t('pro.partner.scanHint'));
    if (!p) return toast(t('pro.partner.noScanner'), 'error');
    run(await p);
  }

  if (!cab.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const c = cab.data;
  const r = result;

  return (
    <main className="app">
      <PageHead title={c.partner.name} back={false} />
      {partners.length > 1 && (
        <div className="bc-chips">
          {partners.map((p) => (
            <Chip key={p.id} pressed={p.id === partnerId} onClick={() => setPartnerId(p.id)}>
              {p.name}
            </Chip>
          ))}
        </div>
      )}
      <div className="bc-small bc-muted">
        {t(c.role === 'OWNER' ? 'pro.partner.owner' : 'pro.partner.staff')} · {t('pro.partner.visits30', { count: c.visits30 })}
      </div>

      <button className="scan-btn" onClick={scan} disabled={check.isPending}>
        <span className="scan-btn__icon" aria-hidden="true" />
        <b>{t('pro.partner.scan')}</b>
        <span>{t('pro.partner.scanSub')}</span>
      </button>
      <form
        className="bc-row"
        style={{ gap: 8 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (manual.trim()) run(manual.trim());
        }}
      >
        <input className="bc-input" style={{ flex: 1 }} value={manual} placeholder={t('pro.partner.manual')} onChange={(e) => setManual(e.target.value)} />
        <Button type="submit" variant="light" disabled={!manual.trim()}>
          {t('pro.partner.check')}
        </Button>
      </form>

      {r && (
        <Card className="bc-stack check-card">
          <div className="check-card__ok">✓</div>
          <h2 className="bc-h3" style={{ margin: 0 }}>
            {t('pro.partner.valid', { name: r.client.firstName, level: LEVEL_NAME[r.level] ?? r.level })}
          </h2>
          {r.endsAt && <div className="bc-small bc-muted">{t('pro.partner.until', { date: dt(r.endsAt, { day: 'numeric', month: 'long' }) })}</div>}
          <ul className="p-offers">
            {r.offers.map((o) => (
              <li key={o.id} data-state={!o.eligible ? 'locked' : o.remaining === 0 ? 'used' : 'ok'}>
                <span>
                  <b>{name(o.title)}</b>
                  <small>
                    {!o.eligible
                      ? t('pro.partner.needLevel', { level: LEVEL_NAME[o.minLevel ?? 'BAREV'] })
                      : o.remaining === null
                        ? t('pro.partner.unlimited')
                        : o.remaining > 0
                          ? t(`bot.partner.left_${o.limit}`, { count: o.remaining })
                          : t(`bot.partner.used_${o.limit}`)}
                  </small>
                </span>
                {o.eligible && o.remaining !== null && o.remaining > 0 && (
                  <Button size="sm" disabled={redeem.isPending} onClick={() => redeem.mutate({ checkId: r.checkId, offerId: o.id })}>
                    {t('bot.partner.redeem')}
                  </Button>
                )}
              </li>
            ))}
            {r.offers.length === 0 && <li>{t('bot.partner.noOffers')}</li>}
          </ul>
        </Card>
      )}

      <Card className="bc-stack">
        <h2 className="bc-h3">{t('pro.partner.offers')}</h2>
        {c.offers.length === 0 && <p className="bc-muted" style={{ margin: 0 }}>{t('pro.partner.noOffers')}</p>}
        {c.offers.map((o) => (
          <div key={o.id} className="bc-between" style={{ gap: 10 }}>
            <span style={{ fontWeight: 600 }}>{name(o.title)}</span>
            <span className="bc-small bc-muted" style={{ textAlign: 'right' }}>
              {o.minLevel ? LEVEL_NAME[o.minLevel] : t('pro.partner.allMembers')}
              {o.limit !== 'NONE' && ` · ${t(`pro.partner.limit_${o.limit}`, { count: o.limitCount })}`}
            </span>
          </div>
        ))}
        <p className="bc-small bc-muted" style={{ margin: 0 }}>
          {t('pro.partner.offersHint')}
        </p>
      </Card>

      {c.role === 'OWNER' && (
        <Card className="bc-stack">
          <div className="bc-between">
            <h2 className="bc-h3">{t('pro.partner.team')}</h2>
            <Button size="sm" onClick={() => makeInvite.mutate({ partnerId })} disabled={makeInvite.isPending}>
              {t('pro.partner.invite')}
            </Button>
          </div>
          {invite && (
            <div className="invite">
              <Qr value={invite.url} size={200} label={t('pro.partner.inviteQr')} />
              <p className="bc-small" style={{ margin: 0, textAlign: 'center' }}>
                {t('pro.partner.inviteHint', { time: dt(invite.expiresAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) })}
              </p>
              <Field label={t('pro.partner.inviteLink')}>
                <input className="bc-input mono" readOnly value={invite.url} onFocus={(e) => e.target.select()} />
              </Field>
            </div>
          )}
          {c.members.map((m) => (
            <div key={m.id} className="bc-between">
              <span>
                <b>{m.name || (m.username ? `@${m.username}` : '—')}</b>
                <span className="bc-small bc-muted"> · {t(m.role === 'OWNER' ? 'pro.partner.owner' : 'pro.partner.staff')}</span>
              </span>
              {m.role === 'STAFF' && (
                <Button size="sm" variant="ghost" onClick={async () => (await confirmDialog(t('pro.partner.removeConfirm', { name: m.name }))) && remove.mutate({ memberId: m.id })}>
                  {t('pro.partner.remove')}
                </Button>
              )}
            </div>
          ))}
        </Card>
      )}

      <Card className="bc-stack">
        <h2 className="bc-h3">{t('pro.partner.recent')}</h2>
        {c.recent.length === 0 && <p className="bc-muted" style={{ margin: 0 }}>{t('pro.partner.noRecent')}</p>}
        {c.recent.map((u) => (
          <div key={u.id} className="bc-between bc-small">
            <span>
              <b>{u.client}</b> · {u.offer ? name(u.offer) : t('pro.partner.checked')}
            </span>
            <span className="bc-muted">
              {LEVEL_NAME[u.level ?? 'BAREV']} · {dt(u.at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
      </Card>
    </main>
  );
}

